import { useState, useCallback, useMemo, useRef } from 'react';
import { Link as RouterLink } from '@tanstack/react-router';
import ProgressiveImage from '@components/progressive-image/ProgressiveImage';
import ProfileModal from '@src/app-pages/Forms/components/modals/profile/Profile';
import MaintenanceBanner from '@components/maintenance-banner/MaintenanceBanner';
import { Button, Header, Menu, PrimaryNav, NavMenuButton, NavDropDownButton } from '@trussworks/react-uswds';
import InternalLink from '@components/internal-link/InternalLink';
import { useModal, useUserRoleCapabilities } from '@stores';
import './navBar.scss';
import { useUserProfile, useAuth, useOutsideClick } from '@hooks';

const MenuItemWrapper = ({ onClick, children: menuItemContent }) => (
  <div
    className='usa-nav__submenu-item'
    onClick={(e) => {
      e.stopPropagation();
      onClick();
    }}
  >
    {menuItemContent}
  </div>
);

const getCurrentPath = () => {
  const path = window.location.pathname;
  return path.endsWith('/') && path.length > 1 ? path.slice(0, -1) : path;
};

const NavBar = () => {
  const { openModal } = useModal();
  const { login, logout, isAuthenticated } = useAuth();
  const [expanded, setExpanded] = useState(false);
  const onClick = () => setExpanded((prevExpanded) => !prevExpanded);
  const [isOpen, setIsOpen] = useState(false);
  const { isLoading, isUserRoleValid, userProfileData } = useUserProfile();
  const userDisplayName = [userProfileData?.firstName, userProfileData?.lastName].filter(Boolean).join(' ');
  const userDisplayRole = userProfileData?.role;
  const userDisplayEmail = userProfileData?.emailAddress;
  const canCreateRequest = useUserRoleCapabilities((state) => state.roleCapabilities.createRequest);
  const canViewDashboard = useUserRoleCapabilities((state) => state.roleCapabilities.viewDashboard);
  const canViewDeveloperTab = useUserRoleCapabilities((state) => state.roleCapabilities.viewDeveloperTab);
  const shouldShowRequestPage = !isLoading && canCreateRequest;
  const shouldShowDashboard = !isLoading && canViewDashboard;
  const shouldShowDeveloperTab = !isLoading && canViewDeveloperTab;

  const userMenuRef = useRef();
  const currentPath = getCurrentPath();

  const isActiveLink = useCallback(
    (linkPath) => {
      if (currentPath === '/') {
        return linkPath === '/' || linkPath === '/request';
      }
      return currentPath === linkPath;
    },
    [currentPath]
  );

  const navLinkClass = useCallback((linkPath) => (isActiveLink(linkPath) ? 'navbar-link-active' : ''), [isActiveLink]);

  const handleKeyPress = useCallback(
    (e) => {
      if (e.key === 'Enter') {
        login();
      }
    },
    [login]
  );

  const handleToggle = useCallback(() => {
    setIsOpen((prevIsOpen) => !prevIsOpen);
  }, []);

  const handleProfileClick = useCallback(() => {
    if (!isLoading && userProfileData) {
      openModal(ProfileModal, { edit: true });
      handleToggle();
    }
    setIsOpen(false);
  }, [isLoading, userProfileData, openModal, handleToggle]);

  const handleLogoutClick = useCallback(() => {
    logout();
    handleToggle();
  }, [logout, handleToggle]);

  useOutsideClick(userMenuRef, () => setIsOpen(false));

  const userMenuItems = useMemo(
    () => [
      <MenuItemWrapper key='profile' onClick={handleProfileClick}>
        <Button className='link-profile'>Profile</Button>
      </MenuItemWrapper>,
      <MenuItemWrapper key='logout' onClick={handleLogoutClick}>
        <Button className='link-logout'>Sign Out</Button>
      </MenuItemWrapper>,
    ],
    [handleProfileClick, handleLogoutClick]
  );

  const navbarLinks = useMemo(() => {
    const defaultLinks = [
      <InternalLink href='/home' key='Home' content='Home' className={`navbar-link-home ${navLinkClass('/home')}`} />,
      <InternalLink
        href='/support'
        key='support'
        content='Support'
        className={`navbar-link-support ${navLinkClass('/support')}`}
      />,
    ];

    return isUserRoleValid
      ? [
          ...defaultLinks,
          shouldShowRequestPage && (
            <InternalLink
              href='/'
              key='Request'
              content='Request Page'
              className={`navbar-link-request ${navLinkClass('/')}`}
            />
          ),
          shouldShowDashboard && (
            <InternalLink
              href='/dashboard'
              key='Dashboard'
              content='Dashboard'
              className={`navbar-link-dashboard ${navLinkClass('/dashboard')}`}
            />
          ),
          shouldShowDeveloperTab && (
            <InternalLink
              href='/developer'
              key='Developer'
              content='Developer'
              className={`navbar-link-developer ${navLinkClass('/developer')}`}
            />
          ),
          <div key='userMenu' ref={userMenuRef} className='user-menu-container'>
            <NavDropDownButton
              menuId='userMenu'
              onToggle={handleToggle}
              isOpen={isOpen}
              label={
                <span className='user-menu-label'>
                  {userDisplayName}
                  {userDisplayRole && <span className='user-menu-role'>({userDisplayRole})</span>}
                  {userDisplayEmail && (
                    <>
                      <span className='user-menu-separator' aria-hidden='true'>
                        ·
                      </span>
                      <span className='user-menu-email'>{userDisplayEmail}</span>
                    </>
                  )}
                </span>
              }
            />
            <Menu items={userMenuItems} isOpen={isOpen} id='userMenuOpen' />
          </div>,
        ]
      : [
          ...defaultLinks,
          <Button className='sign-in' key='sign-in' size='big' onClick={login} onKeyUp={handleKeyPress} tabIndex={0}>
            Sign In
          </Button>,
        ];
  }, [
    isUserRoleValid,
    navLinkClass,
    shouldShowRequestPage,
    shouldShowDashboard,
    shouldShowDeveloperTab,
    login,
    handleToggle,
    isOpen,
    handleKeyPress,
    userMenuItems,
    userDisplayName,
    userDisplayRole,
    userDisplayEmail,
  ]);

  return (
    <div>
      <Header className='navbar' basic>
        <div className='navbar-brand-container'>
          <RouterLink
            to={isAuthenticated ? '/home' : '/'}
            title='Regulatory Request System - Home'
            className='header-logo-link'
          >
            <ProgressiveImage
              className='usace-logo'
              alt='USACE Regulatory Logo'
              localPath='site-graphics/usace-logo-color.svg'
              height={65}
            />
            <ProgressiveImage
              className='rrs-logo'
              alt='RRS Logo'
              localPath='site-graphics/RRS-Black.png'
              hasWebp
              hasAvif
            />
          </RouterLink>
        </div>

        <PrimaryNav items={navbarLinks} mobileExpanded={expanded} onToggleMobileNav={onClick} />
        <NavMenuButton onClick={onClick} label='Menu' />
      </Header>

      <div className='announcement-bar'>
        <div className='logo-spacer' />
        <div className='announcement-container'>
          <div className='announcement-title'></div>
        </div>
      </div>

      <MaintenanceBanner />
    </div>
  );
};

export default NavBar;
